import React, { useMemo, useRef, useState } from "react";

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));
const clampZoom = (v) => Math.max(0.5, Math.min(3, Number(v) || 1));

export default function TemplateBoxAnnotator({
  imageSrc,
  annotations,
  onChange,
  labelOptions,
}) {
  const stageRef = useRef(null);
  const surfaceRef = useRef(null);
  const pointersRef = useRef(new Map());
  const panRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });
  const pinchRef = useRef({
    active: false,
    startDistance: 0,
    startZoom: 1,
  });

  const [zoom, setZoom] = useState(1);
  const [interactionMode, setInteractionMode] = useState("draw");
  const [drawingStart, setDrawingStart] = useState(null);
  const [drawingCurrent, setDrawingCurrent] = useState(null);
  const [pendingLabelTarget, setPendingLabelTarget] = useState(null);
  const [boxInteraction, setBoxInteraction] = useState(null);

  const boxes = useMemo(() => Array.from(annotations || []), [annotations]);

  const toNormPoint = (event) => {
    const surface = surfaceRef.current;
    if (!surface) return null;

    const rect = surface.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return null;

    const x = clamp01((event.clientX - rect.left) / rect.width);
    const y = clamp01((event.clientY - rect.top) / rect.height);
    return { x, y };
  };

  const toBox = (a, b) => {
    const x1 = Math.min(a.x, b.x);
    const y1 = Math.min(a.y, b.y);
    const x2 = Math.max(a.x, b.x);
    const y2 = Math.max(a.y, b.y);
    return {
      x: clamp01(x1),
      y: clamp01(y1),
      w: clamp01(x2 - x1),
      h: clamp01(y2 - y1),
    };
  };

  const updateBox = (id, updater) => {
    onChange(
      boxes.map((row) => {
        if (String(row.id) !== String(id)) return row;
        return { ...row, box: updater(row.box) };
      }),
    );
  };

  const duplicateBox = (row) => {
    const newId = `box-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const duplicate = {
      id: newId,
      label: row.label || "",
      box: {
        x: clamp01(row.box.x + 0.01),
        y: clamp01(row.box.y + 0.01),
        w: row.box.w,
        h: row.box.h,
      },
    };
    onChange([...boxes, duplicate]);
    if (!duplicate.label) {
      setPendingLabelTarget({
        id: newId,
        anchor: {
          x: duplicate.box.x,
          y: clamp01(duplicate.box.y + duplicate.box.h),
        },
      });
    }
  };

  const setLabel = (id, label) => {
    const uniqueLabels = new Set([
      "rowArea",
      "dateTime",
      "participantCount",
      "allianceRanking",
      "alliancePoints",
    ]);

    onChange(
      boxes.map((row) => {
        const isTarget = String(row.id) === String(id);
        if (isTarget) return { ...row, label };

        if (label && uniqueLabels.has(label) && row.label === label) {
          return { ...row, label: "" };
        }

        return row;
      }),
    );
  };

  const deleteBox = (id) => {
    onChange(boxes.filter((row) => String(row.id) !== String(id)));
    if (String(pendingLabelTarget?.id || "") === String(id)) {
      setPendingLabelTarget(null);
    }
    if (String(boxInteraction?.id || "") === String(id)) {
      setBoxInteraction(null);
    }
  };

  const endDrawing = () => {
    setDrawingStart(null);
    setDrawingCurrent(null);
  };

  const endPan = () => {
    panRef.current.active = false;
    panRef.current.pointerId = null;
  };

  const maybeInitPinch = () => {
    const pointers = Array.from(pointersRef.current.values());
    if (pointers.length !== 2) {
      pinchRef.current.active = false;
      return;
    }
    const [a, b] = pointers;
    const distance = Math.hypot(a.x - b.x, a.y - b.y);
    if (!distance || !Number.isFinite(distance)) return;
    pinchRef.current = {
      active: true,
      startDistance: distance,
      startZoom: zoom,
    };
  };

  if (!imageSrc) {
    return (
      <div className="events-annotator-empty">
        Upload a base screenshot to start labeling.
      </div>
    );
  }

  const draftBox = drawingStart && drawingCurrent ? toBox(drawingStart, drawingCurrent) : null;

  return (
    <div className="events-annotator-wrap">
      <div className="events-annotator-toolbar">
        <div className="events-annotator-toolbar-group">
          <button
            type="button"
            className={interactionMode === "draw" ? "active" : ""}
            aria-pressed={interactionMode === "draw"}
            onClick={() => setInteractionMode("draw")}
          >
            Draw
          </button>
          <button
            type="button"
            className={interactionMode === "pan" ? "active" : ""}
            aria-pressed={interactionMode === "pan"}
            onClick={() => setInteractionMode("pan")}
          >
            Pan / Zoom
          </button>
        </div>

        <div className="events-annotator-toolbar-group">
          <button type="button" onClick={() => setZoom((z) => clampZoom(z - 0.1))}>−</button>
          <input
            type="range"
            min={0.5}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(clampZoom(e.target.value))}
          />
          <button type="button" onClick={() => setZoom((z) => clampZoom(z + 0.1))}>+</button>
          <button
            type="button"
            onClick={() => {
              setZoom(1);
              const stage = stageRef.current;
              if (stage) {
                stage.scrollLeft = 0;
                stage.scrollTop = 0;
              }
            }}
          >
            Reset View
          </button>
          <span>{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      <div className="events-annotator-hint">
        {interactionMode === "draw"
          ? "Draw mode: drag to create a box. Drag label to move. Drag corner to resize."
          : "Pan mode: drag canvas to move. Pinch or use +/- to zoom."}
      </div>

      <div
        ref={stageRef}
        className={`events-annotator-stage ${interactionMode === "pan" ? "is-pan" : "is-draw"}`}
        onPointerDown={(e) => {
          pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pointersRef.current.size >= 2) {
            maybeInitPinch();
            return;
          }

          if (pendingLabelTarget && !e.target.closest(".events-annotator-popover")) {
            setPendingLabelTarget(null);
          }

          if (e.target.closest(".events-annotator-popover")) return;
          if (e.target.closest(".events-annotator-box-control")) return;
          if (e.target.closest(".events-annotator-box")) return;

          const stage = stageRef.current;
          if (!stage) return;

          if (interactionMode === "pan") {
            e.preventDefault();
            stage.setPointerCapture?.(e.pointerId);
            panRef.current = {
              active: true,
              pointerId: e.pointerId,
              startX: e.clientX,
              startY: e.clientY,
              startScrollLeft: stage.scrollLeft,
              startScrollTop: stage.scrollTop,
            };
            return;
          }

          e.preventDefault();
          stage.setPointerCapture?.(e.pointerId);
          const point = toNormPoint(e);
          if (!point) return;
          setDrawingStart({ x: point.x, y: point.y });
          setDrawingCurrent({ x: point.x, y: point.y });
        }}
        onPointerMove={(e) => {
          pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

          if (pinchRef.current.active && pointersRef.current.size >= 2) {
            e.preventDefault();
            const points = Array.from(pointersRef.current.values());
            const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
            if (!distance || !Number.isFinite(distance)) return;
            const ratio = distance / (pinchRef.current.startDistance || distance);
            setZoom(clampZoom((pinchRef.current.startZoom || 1) * ratio));
            return;
          }

          if (panRef.current.active && panRef.current.pointerId === e.pointerId) {
            e.preventDefault();
            const stage = stageRef.current;
            if (!stage) return;
            const dx = e.clientX - panRef.current.startX;
            const dy = e.clientY - panRef.current.startY;
            stage.scrollLeft = panRef.current.startScrollLeft - dx;
            stage.scrollTop = panRef.current.startScrollTop - dy;
            return;
          }

          const point = toNormPoint(e);
          if (!point) return;

          if (boxInteraction) {
            e.preventDefault();
            const dx = point.x - boxInteraction.start.x;
            const dy = point.y - boxInteraction.start.y;

            if (boxInteraction.mode === "move") {
              updateBox(boxInteraction.id, (origin) => {
                const maxX = Math.max(0, 1 - origin.w);
                const maxY = Math.max(0, 1 - origin.h);
                return {
                  ...origin,
                  x: Math.max(0, Math.min(maxX, boxInteraction.origin.x + dx)),
                  y: Math.max(0, Math.min(maxY, boxInteraction.origin.y + dy)),
                };
              });
              return;
            }

            if (boxInteraction.mode === "resize") {
              updateBox(boxInteraction.id, () => {
                const minW = 0.01;
                const minH = 0.01;
                const nextW = Math.max(
                  minW,
                  Math.min(1 - boxInteraction.origin.x, boxInteraction.origin.w + dx),
                );
                const nextH = Math.max(
                  minH,
                  Math.min(1 - boxInteraction.origin.y, boxInteraction.origin.h + dy),
                );
                return {
                  ...boxInteraction.origin,
                  w: nextW,
                  h: nextH,
                };
              });
            }
            return;
          }

          if (!drawingStart) return;
          e.preventDefault();
          setDrawingCurrent({ x: point.x, y: point.y });
        }}
        onPointerUp={(e) => {
          pointersRef.current.delete(e.pointerId);
          if (pointersRef.current.size < 2) pinchRef.current.active = false;

          if (panRef.current.active && panRef.current.pointerId === e.pointerId) {
            e.preventDefault();
            endPan();
            return;
          }

          if (boxInteraction) {
            e.preventDefault();
            setBoxInteraction(null);
            return;
          }

          if (!drawingStart) return;
          e.preventDefault();
          const point = toNormPoint(e);
          if (!point) {
            endDrawing();
            return;
          }

          const box = toBox(drawingStart, { x: point.x, y: point.y });
          endDrawing();
          if (box.w < 0.01 || box.h < 0.01) return;

          const id = `box-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          onChange([...boxes, { id, label: "", box }]);
          setPendingLabelTarget({
            id,
            anchor: { x: box.x, y: clamp01(box.y + box.h) },
          });
        }}
        onPointerCancel={(e) => {
          pointersRef.current.delete(e.pointerId);
          pinchRef.current.active = false;
          if (panRef.current.pointerId === e.pointerId) endPan();
          if (drawingStart) endDrawing();
          if (boxInteraction) setBoxInteraction(null);
        }}
      >
        <div
          ref={surfaceRef}
          className="events-annotator-surface"
          style={{ width: `${zoom * 100}%` }}
        >
          <img
            src={imageSrc}
            alt="Template base"
            className="events-annotator-image"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />

          {boxes.map((row) => (
            <div
              key={row.id}
              className="events-annotator-box"
              style={{
                left: `${row.box.x * 100}%`,
                top: `${row.box.y * 100}%`,
                width: `${row.box.w * 100}%`,
                height: `${row.box.h * 100}%`,
              }}
            >
              <div
                className="events-annotator-box-label events-annotator-box-control"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const point = toNormPoint(e);
                  if (!point) return;

                  const shouldDuplicate = e.ctrlKey || e.metaKey;
                  if (shouldDuplicate) {
                    const newId = `box-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                    const duplicate = {
                      id: newId,
                      label: row.label || "",
                      box: { ...row.box },
                    };
                    onChange([...boxes, duplicate]);
                    if (!duplicate.label) {
                      setPendingLabelTarget({
                        id: newId,
                        anchor: {
                          x: duplicate.box.x,
                          y: clamp01(duplicate.box.y + duplicate.box.h),
                        },
                      });
                    } else {
                      setPendingLabelTarget(null);
                    }
                    setBoxInteraction({
                      mode: "move",
                      id: newId,
                      start: { x: point.x, y: point.y },
                      origin: { ...row.box },
                    });
                    return;
                  }

                  setPendingLabelTarget(null);
                  setBoxInteraction({
                    mode: "move",
                    id: row.id,
                    start: { x: point.x, y: point.y },
                    origin: { ...row.box },
                  });
                }}
                title="Drag to move box"
              >
                <span>{row.label || "Choose label"}</span>
                <button
                  type="button"
                  className="events-annotator-box-dup"
                  title="Duplicate box"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    duplicateBox(row);
                  }}
                >
                  ⧉
                </button>
                <button
                  type="button"
                  className="events-annotator-box-trash"
                  title="Delete box"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteBox(row.id);
                  }}
                >
                  🗑️
                </button>
              </div>

              <div
                className="events-annotator-resize-handle events-annotator-box-control"
                title="Drag to resize"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const point = toNormPoint(e);
                  if (!point) return;
                  setPendingLabelTarget(null);
                  setBoxInteraction({
                    mode: "resize",
                    id: row.id,
                    start: { x: point.x, y: point.y },
                    origin: { ...row.box },
                  });
                }}
              />
            </div>
          ))}

          {draftBox && (
            <>
              <div
                className="events-annotator-box events-annotator-box-draft"
                style={{
                  left: `${draftBox.x * 100}%`,
                  top: `${draftBox.y * 100}%`,
                  width: `${draftBox.w * 100}%`,
                  height: `${draftBox.h * 100}%`,
                }}
              />
              <div
                className="events-annotator-dimensions"
                style={{
                  left: `${(drawingCurrent?.x ?? draftBox.x) * 100}%`,
                  top: `${(drawingCurrent?.y ?? draftBox.y) * 100}%`,
                }}
              >
                {Math.round(draftBox.w * 100)}% × {Math.round(draftBox.h * 100)}%
              </div>
            </>
          )}

          {pendingLabelTarget?.anchor && (
            <div
              className="events-annotator-popover"
              style={{
                left: `${pendingLabelTarget.anchor.x * 100}%`,
                top: `${pendingLabelTarget.anchor.y * 100}%`,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <select
                autoFocus
                value={boxes.find((b) => String(b.id) === String(pendingLabelTarget.id))?.label || ""}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setLabel(pendingLabelTarget.id, e.target.value);
                  if (e.target.value) setPendingLabelTarget(null);
                }}
              >
                <option value="">Label this box…</option>
                {labelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => setPendingLabelTarget(null)}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      {!!boxes.length && (
        <div className="events-annotator-list">
          {boxes.map((row) => (
            <div key={row.id} className="events-annotator-list-item">
              <select value={row.label || ""} onChange={(e) => setLabel(row.id, e.target.value)}>
                <option value="">Choose label</option>
                {labelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span>
                x:{row.box.x.toFixed(3)} y:{row.box.y.toFixed(3)} w:{row.box.w.toFixed(3)} h:{row.box.h.toFixed(3)}
              </span>
              <div className="events-annotator-list-actions">
                <button onClick={() => duplicateBox(row)}>Duplicate</button>
                <button onClick={() => deleteBox(row.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
